import { readRhythmClock, type RhythmClock } from "./clock";
import {
  calculateRhythmResult,
  type RhythmResult,
  type RhythmTapResult
} from "./scoring";
import { normalizeRhythmToken, type RhythmPayload } from "./schema";

export type RhythmPhase =
  | "ready"
  | "model-playing"
  | "awaiting-input"
  | "completed";

export type RhythmCuePresentation = "pulse" | "static";

export interface RhythmState {
  readonly phase: RhythmPhase;
  readonly caption: string;
  readonly reducedMotion: boolean;
  readonly cuePresentation: RhythmCuePresentation;
  readonly taps: readonly RhythmTapResult[];
  readonly replayCount: number;
  readonly attemptStartedAtMs: number | null;
  readonly inputStartedAtMs: number | null;
  readonly mediaUnavailable: boolean;
  readonly result: RhythmResult | null;
}

export type RhythmAction =
  | { readonly type: "start" }
  | { readonly type: "model-ended" }
  | { readonly type: "media-unavailable" }
  | { readonly type: "tap"; readonly token: string }
  | { readonly type: "replay" }
  | { readonly type: "reset" };

export interface CreateRhythmStateOptions {
  readonly reducedMotion?: boolean;
}

export function createRhythmState(
  payload: RhythmPayload,
  options: CreateRhythmStateOptions = {}
): RhythmState {
  const reducedMotion = options.reducedMotion ?? false;

  return {
    phase: "ready",
    caption: payload.caption,
    reducedMotion,
    cuePresentation: reducedMotion ? "static" : "pulse",
    taps: [],
    replayCount: 0,
    attemptStartedAtMs: null,
    inputStartedAtMs: null,
    mediaUnavailable: false,
    result: null
  };
}

function startModelPlayback(
  state: RhythmState,
  clock: RhythmClock,
  replayCount: number
): RhythmState {
  return {
    ...state,
    phase: "model-playing",
    taps: [],
    replayCount,
    attemptStartedAtMs: readRhythmClock(clock),
    inputStartedAtMs: null,
    mediaUnavailable: false,
    result: null
  };
}

function recordTap(
  state: RhythmState,
  payload: RhythmPayload,
  token: string,
  clock: RhythmClock
): RhythmState {
  if (state.phase !== "awaiting-input") {
    return state;
  }

  const index = state.taps.length;
  const expectedToken = payload.answerTokens[index];
  const expectedCueMs = payload.beatCuesMs[index];

  if (expectedToken === undefined || expectedCueMs === undefined) {
    return state;
  }

  const now = readRhythmClock(clock);
  const inputStartedAtMs = state.inputStartedAtMs ?? now;
  const offsetMs = Math.max(0, now - inputStartedAtMs);
  const errorMs = Math.abs(offsetMs - expectedCueMs);
  const tokenCorrect =
    normalizeRhythmToken(token) === normalizeRhythmToken(expectedToken);
  const tap: RhythmTapResult = {
    token,
    expectedToken,
    offsetMs,
    errorMs,
    tokenCorrect,
    onBeat: tokenCorrect && errorMs <= payload.toleranceMs
  };
  const taps = [...state.taps, tap];

  if (taps.length < payload.answerTokens.length) {
    return {
      ...state,
      taps,
      inputStartedAtMs
    };
  }

  const attemptStartedAtMs = state.attemptStartedAtMs ?? inputStartedAtMs;
  const result = calculateRhythmResult(
    payload,
    taps,
    state.replayCount,
    Math.max(0, now - attemptStartedAtMs)
  );

  return {
    ...state,
    phase: "completed",
    taps,
    inputStartedAtMs,
    result
  };
}

export function reduceRhythmState(
  state: RhythmState,
  payload: RhythmPayload,
  action: RhythmAction,
  clock: RhythmClock
): RhythmState {
  switch (action.type) {
    case "reset":
      return createRhythmState(payload, {
        reducedMotion: state.reducedMotion
      });
    case "start":
      return state.phase === "ready"
        ? startModelPlayback(state, clock, state.replayCount)
        : state;
    case "replay":
      return state.phase === "ready"
        ? state
        : startModelPlayback(state, clock, state.replayCount + 1);
    case "model-ended":
      return state.phase === "model-playing"
        ? { ...state, phase: "awaiting-input" }
        : state;
    case "media-unavailable":
      return state.phase === "model-playing"
        ? {
            ...state,
            phase: "awaiting-input",
            mediaUnavailable: true
          }
        : state;
    case "tap":
      return recordTap(state, payload, action.token, clock);
  }
}
